import { take, takeEvery, takeLatest, put, call, fork, select } from 'redux-saga/effects'

import { indicateProgress } from '../api'
import { LOCATION_CHANGE } from '../browser-location'
import { languageSelector } from '../i18n/selectors'

import * as api from './api'
import * as actions from './actions'
import * as constants from './constants'
import { pendingName } from './selectors'
import { setTheming } from 'settings/utils'

/* Configuration sagas */

export function* configurationSaga() {
    yield takeLatest(constants.REQUEST_CONFIGURATION, function* ({ language }) {
        const configuration = yield call(indicateProgress, ['configuration'], api.getConfiguration, language)

        yield put(actions.configurationReceived(configuration))
        window.prerenderReady = true
        const { result } = configuration

        if (result) {
            setTheming(result.themeSettings)
        }
    })
    yield put(actions.requestConfiguration(yield select(languageSelector)))
}

export function* updateConfigurationBasedOnLanguageSaga() {
    let previousLanguage = yield select(languageSelector)

    yield takeLatest(LOCATION_CHANGE, function* () {
        const newLanguage = yield select(languageSelector)

        if (previousLanguage !== newLanguage) yield put(actions.requestConfiguration(newLanguage))

        previousLanguage = newLanguage
    })
}

/* Content sagas */

export function* sendFeedbackRequest({ data }) {
    try {
        yield call(indicateProgress, ['sendFeedback'], api.sendFeedback, data)
    } catch (exception) {}
}

export function* feedbackSaga() {
    yield takeEvery(constants.SEND_FEEDBACK, sendFeedbackRequest)
}

export function* submitFormSaga() {
    yield takeEvery(constants.SUBMIT_FORM, function* ({ data }) {
        try {
            yield call(indicateProgress, ['submitForm'], api.submitForm, data)
        } catch (exception) {}
    })
}

export function* watchPostFetchRequest({ kind, postIDs = [] }) {
    try {
        const language = yield select(languageSelector)
        const posts = yield call(
            indicateProgress,
            postIDs.map(id => pendingName(kind, id)),
            api.fetchPosts,
            kind,
            postIDs,
            language
        )
        yield put(actions.postsReceived(posts, kind))
    } catch (exception) {}
}

export function* postsSaga() {
    yield takeEvery(constants.FETCH_POSTS, watchPostFetchRequest)
}

export function* watchPostQueryRequest({ kind, params, requestId }) {
    try {
        const {
            result: { posts, paging },
            entities,
        } = yield call(indicateProgress, [pendingName(kind)], api.queryPosts, kind, params)

        yield put(
            actions.postsReceived(
                {
                    entities,
                    result: {
                        posts,
                        paging: {
                            ...paging,
                            page: parseInt(params.page),
                        },
                    },
                },
                kind,
                requestId
            )
        )
    } catch (exception) {}
}

export function* postsQuerySaga() {
    yield takeEvery(constants.QUERY_POSTS, watchPostQueryRequest)
}

const subscriptionSagaFactory = (event, progressName, apiCall, successAction) =>
    function* subscriptionSaga() {
        while (true)
            try {
                const { email } = yield take(event)
                const { message } = yield call(indicateProgress, [progressName], apiCall, email)
                yield put(successAction(message))
            } catch (exception) {}
    }

const newsSubscriptionSaga = subscriptionSagaFactory(
    constants.REQUEST_SUBSCRIBE_NEWS,
    'newsletterSubscription',
    api.subscribeNews,
    actions.subscribeNewsSucceeded
)

const offersSubscriptionSaga = subscriptionSagaFactory(
    constants.REQUEST_SUBSCRIBE_OFFERS,
    'offersSubscription',
    api.subscribeOffers,
    actions.subscribeOffersSucceeded
)

export function* rootSaga() {
    yield fork(configurationSaga)
    yield fork(updateConfigurationBasedOnLanguageSaga)
    yield fork(submitFormSaga)
    yield fork(feedbackSaga)
    yield fork(postsSaga)
    yield fork(postsQuerySaga)
    yield fork(newsSubscriptionSaga)
    yield fork(offersSubscriptionSaga)
}
